import './Configurator.css';
import React, { useEffect, useState } from 'react';
import {
  Typography,
  TextField,
  Button,
  Box,
  Divider,
  Menu,
  MenuItem,
  Tabs,
  Tab,
  Alert
} from '@mui/material';
import AddBookIcon from '@mui/icons-material/MenuBook';
import ImportContactsIcon from '@mui/icons-material/ImportContacts';
import DraftsTable from '../components/DraftsTable';
import OrdersTable from '../components/OrdersTable';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { styled, alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../axiosConfig';
import API_URL from '../config';

const StyledMenu = styled((props) => (
  <Menu
    elevation={3}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: '0 0 8px 8px',
    marginTop: 0,
    minWidth: 180,
    color: theme.palette.text.primary,
    backgroundColor: '#fff', // Hintergrundfarbe bleibt weiß
    border: '1px solid #ccc', // Rahmen um das Menü
    boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 6px -2px, rgba(0, 0, 0, 0.05) 0px 10px 15px -3px',
  },
  '& .MuiMenuItem-root': {
    '& .MuiSvgIcon-root': { fontSize: 20, color: theme.palette.text.secondary, marginRight: theme.spacing(1.5) },
    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) }, // Dezenter Hover-Effekt
  },
}));


const Configurator = () => {
  const [books, setBooks] = useState([]);
  const [printJobs, setPrintJobs] = useState([]);
  const [error, setError] = useState('');
  const [newBookName, setNewBookName] = useState('');
  const [isDuplicateName, setIsDuplicateName] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [buttonWidth, setButtonWidth] = useState(null);
  const isMenuOpen = Boolean(menuAnchorEl);
  const navigate = useNavigate();

  const handleMenuClick = (event) => {
    setMenuAnchorEl(event.currentTarget);
    setButtonWidth(event.currentTarget.offsetWidth);
  };
  
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };  

  const handleCreateFromTemplate = async (templateId) => {
    const token = localStorage.getItem('authToken');
    try {
      // Neues Buch durch Duplizieren der Vorlage erstellen
      const response = await axiosInstance.post(
        '/books/create-from-template',
        { templateId, newBookName },
        { headers: { Authorization: `${token}` } }
      );
  
      const newBookId = response.data.bookId;
  
      // Buch bearbeiten
      handleEditBook(newBookId);
    } catch (error) {
      console.error('Fehler beim Erstellen eines Buches aus Vorlage:', error);
    }
    handleMenuClose();
  };  

  const fetchTemplates = async () => {
    const token = localStorage.getItem('authToken');
    try {
      const response = await axiosInstance.get(
        '/books/templates',
        { headers: { Authorization: `${token}` } }
      );
      setTemplates(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Vorlagen:', error);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  // Bücher aus der Datenbank laden
  useEffect(() => {
    fetchBooks();
    fetchPrintJobs();
  }, []);

  const fetchBooks = async () => {
    const token = localStorage.getItem('authToken');
    try {
      const response = await axiosInstance.get(
        '/profile/books',
        { headers: { Authorization: `${token}` } }
      );
      setBooks(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Bücher:', error);
    }
  };

  const fetchPrintJobs = async () => {
    const token = localStorage.getItem('authToken');
    try {
      const response = await axiosInstance.get(
        '/doc/print-jobs',
        { headers: { Authorization: `${token}` } }
      );
  
      // Sortiere die printJobs absteigend nach jobId
      const sortedPrintJobs = response.data.sort((a, b) => b.jobId - a.jobId);
  
      setPrintJobs(sortedPrintJobs);
    } catch (error) {
      console.error('Fehler beim Laden der Druckaufträge:', error);
    }
  };  

  const handlePrintBook = async (bookId) => {
    navigate('/pages/review', { state: { bookId } });
  };

  const handleBookNameChange = (event) => {
    const inputName = event.target.value;
    const invalidChars = /[^a-zA-Z0-9äöüÄÖÜß_-]/g;
    const containsInvalidChars = invalidChars.test(inputName);
  
    setNewBookName(inputName);
    
    // Reset error text if user input is changed
    if (error) setError('');
  
    // Prüfe, ob der Name bereits existiert
    const isDuplicate = books.some((book) => book.name.toLowerCase() === inputName.toLowerCase());
    setIsDuplicateName(isDuplicate);
  
    // Fehler setzen, falls unerlaubte Zeichen vorhanden sind
    if (containsInvalidChars) {
      setError("Nur Buchstaben, Zahlen, Bindestriche und Unterstriche sind erlaubt.");
    }
  };

  // Neues Buch erstellen
  const handleCreateNewBook = () => {
    navigate('/pages/QuestionForm', { state: { isCreating: true, bookName: newBookName } });
  };

  // Vorhandenes Buch bearbeiten
  const handleEditBook = (bookId) => {
    navigate('/pages/ComponentResults/', { state: { bookId } })
  }

  const handleEditBookWithNewSelection = async (bookId, bookName) => {
    const token = localStorage.getItem('authToken');
    try {
      // Buchdaten laden
      const response = await axiosInstance.get(
        `/books/${bookId}`,
        { headers: { Authorization: `${token}` } }
      );
  
      const bookData = response.data;
  
      // Navigiere zu QuestionForm und übergib die Daten
      navigate('/pages/QuestionForm', {
        state: {
          bookId,
          bookName,
          primaryCategory: bookData.primaryCategory, // Übergebe die primäre Kategorie
          isCreating: false,
          editWithNewSelection: true,
        },
      });
    } catch (error) {
      console.error('Fehler beim Laden der Buchdaten:', error);
    }
  };  

  // Buch löschen
  const handleDeleteBook = async (bookId) => {
    const token = localStorage.getItem('authToken');
    try {
      await axiosInstance.delete(
        `/books/${bookId}`,
        { headers: { Authorization: `${token}` } }
      );
      fetchBooks(); // Tabelle aktualisieren
    } catch (error) {
      console.error('Fehler beim Löschen des Buches:', error);
    }
  };

  return (
    <Box sx={{ padding: '20px', maxWidth: '900px' }}>
      <Box sx={{ border: '1px solid #ccc', borderRadius: '8px', mt: 2.9, ml: 1.3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '50px',
            borderBottom: '2px solid #e0e0e0',
            backgroundColor: '#f9f9f9',
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: '#333',
              letterSpacing: '0.5px'
            }}
          >
            Schullogbuch Konfigurator
          </Typography>
        </Box>
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', marginLeft: '1rem', marginRight: '2rem'}}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem', width: '100%' }}>
        <TextField
          sx={{ mt: '1rem', flex: '1' }}
          id="outlined-basic"
          label="Name des Schullogbuchs"
          variant="outlined"
          value={newBookName}
          onChange={handleBookNameChange}
          error={!!error || isDuplicateName} // Fehleranzeige bei Duplikaten
          />
          <Button
            id="template-button"
            startIcon={<AddBookIcon style={{ fontSize: 30 }} />}
            aria-controls={isMenuOpen ? 'template-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={isMenuOpen ? 'true' : undefined}
            variant="contained"
            disableElevation
            disabled={!newBookName.trim() || error || isDuplicateName}
            onClick={handleMenuClick}
            endIcon={<KeyboardArrowDownIcon />}
            color='primary'
            sx={{
              backgroundColor: 'primary.light',
              color: '#fff',
              mt: '1rem',
              height: '56px',
              fontSize: '14px',
              padding: '16.5px 14px',
              justifyContent: 'flex-start',
              border: 'none',
              borderRadius: isMenuOpen ? '4px 4px 0 0' : '4px',
              '&:hover': {backgroundColor: 'primary.dark', color: '#fff'} // Etwas dunkler beim Hover
            }}
          >
            Aus Vorlage erstellen
          </Button>
          <StyledMenu
            id="template-menu"
            MenuListProps={{ 'aria-labelledby': 'template-button' }}
            anchorEl={menuAnchorEl}
            open={isMenuOpen}
            onClose={handleMenuClose}
            // sx={{
            //   '& .MuiPaper-root': {
            //     width: buttonWidth ? `calc(${buttonWidth}px - 2px)` : 'auto',
            //     maxWidth: '100%',
            //   },
            // }}
          >
            {templates.length > 0 ? (
              templates.map((template) => (
                <MenuItem key={template.id} onClick={() => handleCreateFromTemplate(template._id)} disableRipple>
                  {template.name}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>Keine Vorlagen verfügbar</MenuItem>
            )}
          </StyledMenu>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ImportContactsIcon style={{ fontSize: 30 }} />}
            onClick={handleCreateNewBook}
            disabled={!newBookName.trim() || error || isDuplicateName} // Button deaktivieren, wenn der Name leer ist
            sx={{
              color: 'primary.light',
              mt: '1rem',
              height: '56px',
              fontSize: '14px',
              padding: '16.5px 14px',
              justifyContent: 'flex-start',
              border: '1px solid primary.light',
              '&:hover': {backgroundColor: 'primary.dark', color: '#fff'}, // Etwas dunkler beim Hover
              marginRight: '2rem'
            }}
          >
            Neu erstellen
          </Button>
        </Box>
        {(error || isDuplicateName) && (
          <Box sx= {{ mt: '1rem', mr: '2rem'}}>
            <Alert severity="error">
              {error
                ? error
                : "Dieser Name wird bereits in einem Ihrer Entwürfe verwendet. Bitte vergeben Sie einen neuen Namen."}
            </Alert>
          </Box>
          )}
        </Box>
        <Divider sx={{ mt: '1rem' }}/>
        <Tabs
          value={tabIndex}
          onChange={(event, newValue) => setTabIndex(newValue)}
          aria-label="Konfigurator Tabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ mt: '1rem', ml: '1rem' }}
        >
          <Tab label="Entwürfe" />
          <Tab label="Aufträge" />
        </Tabs>

        {tabIndex === 0 && <DraftsTable books={books} handleEditBook={handleEditBook} handleEditBookWithNewSelection={handleEditBookWithNewSelection} handleDeleteBook={handleDeleteBook} handlePrintBook={handlePrintBook} />}
        {tabIndex === 1 && <OrdersTable printJobs={printJobs} />}
      </Box>
    </Box>
  );
};

export default Configurator;
